/**
 * LoadingScreen — Full-page loading spinner with a 6-second escape hatch.
 *
 * Problem this solves: in Production, when MongoDB Atlas is overloaded,
 * /api/auth/me can take 20+ seconds, leaving the user staring at an
 * infinite-looking spinner. After 6 seconds we surface a clear message
 * ("Conexión lenta — Reintentar") so the user has agency instead of
 * hammering F5 blindly.
 */
import { useEffect, useState } from "react";
import { CircleNotch, ArrowsClockwise, WifiSlash } from "@phosphor-icons/react";

export default function LoadingScreen() {
  const [slowConnection, setSlowConnection] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSlowConnection(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-6"
      data-testid="loading-screen"
    >
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <CircleNotch size={40} className="animate-spin text-primary" />
        {!slowConnection ? (
          <p className="text-xs text-muted-foreground">Cargando…</p>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-center gap-2 text-amber-400">
              <WifiSlash size={18} />
              <p className="text-sm font-semibold">Conexión lenta</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              El servidor está tardando más de lo normal en responder.
              Esto suele resolverse en pocos segundos.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="loading-retry-btn"
            >
              <ArrowsClockwise size={14} /> Reintentar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
